/**
 * @graphql cc
 */
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { gql } from '@apollo/client';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup';
import { Box, Pressable, Text, Flex } from '@atlaskit/primitives/compiled';
import { cssMap, cx } from '@atlaskit/css';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import DatabaseIcon from '@atlaskit/icon/core/database';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { IconTile } from '@atlaskit/icon';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { getFullWhiteboardUrl } from '@confluence/whiteboard-extension/entry-points/getFullWhiteboardUrl.ts';
import { getFullDatabaseUrl } from '@confluence/confluence-databases-extension/entry-points/getFullDatabaseUrl.ts';
import {
	useGetOnboardingState,
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import {
	PROMPT_WHITEBOARD_COMPETITOR_LINK_DISMISSED,
	PROMPT_DATABASE_COMPETITOR_LINK,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { useIsUrlWhiteboardCompetitor } from './useIsUrlWhiteboardCompetitor.ts';
import { useIsEligibleDatabaseCompetitor } from './useIsEligibleDatabaseCompetitor.ts';
import { usePageAnalyticsAttributes } from './usePageAnalyticsAttributes.ts';
import { i18n as databaseI18n } from './PromptDatabase.tsx';
import type {
	PromptPopupCreateContentInlineMutation as PromptPopupCreateContentInlineMutation$data,
	PromptPopupCreateContentInlineMutationVariables as PromptPopupCreateContentInlineMutation$variables,
} from './__types__/PromptPopupCreateContentInlineMutation.js';
import type {
	PromptPopupPaywallContentToDisableQuery as PromptPopupPaywallContentToDisableQuery$data,
	PromptPopupPaywallContentToDisableQueryVariables as PromptPopupPaywallContentToDisableQuery$variables,
} from './__types__/PromptPopupPaywallContentToDisableQuery.js';

export const i18n = defineMessages({
	titleWhiteboard: {
		id: 'prompt-competitor-link.prompt-popup.title-whiteboard',
		defaultMessage: 'Try Confluence whiteboards',
		description: 'Popup title to bring awareness to the whiteboards feature',
	},
	descriptionDefaultWhiteboard: {
		id: 'prompt-competitor-link.prompt-popup.description-default-whiteboard',
		defaultMessage:
			'We noticed you pasted a {competitorName} link. Reduce context switching by using a Confluence whiteboard instead.',
		description:
			'Context which explains that whiteboards are a better alternative to the detected competitor as it keeps all work within Confluence',
	},
	descriptionImportWhiteboard: {
		id: 'prompt-competitor-link.prompt-popup.description-import-whiteboard',
		defaultMessage:
			'We noticed you pasted a {competitorName} link. Reduce context switching by importing your work to a Confluence whiteboard instead.',
		description:
			'Context which explains that whiteboards are a better alternative to the detected competitor as it keeps all work within Confluence and brings awareness of the Importer feature',
	},
	createButtonDefaultWhiteboard: {
		id: 'prompt-competitor-link.prompt-popup.create-button-default-whiteboard',
		defaultMessage: 'Create a whiteboard',
		description: 'Button to create and redirect the user to a new whiteboard',
	},
	createButtonImportWhiteboard: {
		id: 'prompt-competitor-link.prompt-popup.create-button-import-whiteboard',
		defaultMessage: 'Import from {competitorName}',
		description:
			'Button to create and redirect the user to a new whiteboard as well as open the Importer',
	},
	dismissButton: {
		id: 'prompt-competitor-link.prompt-popup.dismiss-button',
		defaultMessage: 'Do not show whiteboards again',
		description:
			'Button to close the popup and stop displaying the prompt icon and popup everywhere on the current page and not show it again for whiteboards variant of the prompt',
	},
});

const styles = cssMap({
	dismissPressable: {
		backgroundColor: token('elevation.surface.overlay'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	iconContainer: {
		gap: token('space.200'),
	},
	titleContainer: {
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	pressable: {
		width: '24px',
		height: '24px',
		padding: token('space.050'),
		borderRadius: token('radius.small'),
	},
	pressableClosed: {
		backgroundColor: token('color.background.accent.purple.subtlest'),
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		},
	},
	pressableOpen: {
		backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		outline: `${token('space.025')} solid ${token('color.border.selected')}`,
		outlineOffset: token('space.negative.025'),
	},
	promptContainer: {
		display: 'inline-flex',
		alignItems: 'center',
		marginRight: token('space.025'),
		marginLeft: token('space.025'),
	},
	popupBox: {
		width: '360px',
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
		marginLeft: token('space.300'),
		marginRight: token('space.300'),
	},
	popupBoxDatabase: {
		width: '360px',
		margin: token('space.200'),
	},
	popupDescription: {
		marginTop: token('space.100'),
		marginBottom: token('space.200'),
	},
	showMoreIcon: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
	textContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
	},
	ctaStyles: {
		color: token('color.link'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		margin: token('space.0'),
		textDecoration: 'underline',
		font: token('font.body'),
		'&:hover': {
			textDecoration: 'none',
		},
	},
});

const CREATE_CONTENT_INLINE = gql`
	mutation PromptPopupCreateContentInlineMutation(
		$createdInContentId: ID!
		$spaceKey: String
		$type: String!
	) {
		createContentInline(
			input: { createdInContentId: $createdInContentId, spaceKey: $spaceKey, type: $type }
		) {
			id
		}
	}
`;

const PAYWALL_CONTENT_TO_DISABLE = gql`
	query PromptPopupPaywallContentToDisableQuery {
		paywallContentToDisable(contentType: "whiteboard") {
			deactivationIdentifier
			link
			title
		}
	}
`;

export type LinkType = 'inline' | 'card' | 'embed';

export type PromptContentType = 'whiteboard' | 'database';

const CONTENT_TYPE_URL_FUNCTIONS: Record<
	PromptContentType,
	(contentId: string, spaceKey: string) => string
> = {
	whiteboard: getFullWhiteboardUrl,
	database: getFullDatabaseUrl,
};

export type PromptComponentContainerProps = {
	sourceUrl: string;
	linkType?: string;
	editorAppearance?: string;
};

type PromptComponentProps = {
	competitorName: string;
	linkType?: LinkType;
	contentType: PromptContentType;
	editorAppearance?: string;
};

const IMPORTER_COMPETITORS = ['miro', 'mural'];

const CONTENT_TYPE_ONBOARDING_KEYS: Record<PromptContentType, string> = {
	whiteboard: PROMPT_WHITEBOARD_COMPETITOR_LINK_DISMISSED.PROMPT_DISMISSED,
	database: PROMPT_DATABASE_COMPETITOR_LINK.DISMISSED,
};

const PromptComponent = ({
	competitorName,
	linkType,
	contentType,
	editorAppearance,
}: PromptComponentProps) => {
	const intl = useIntl();
	const { edition } = useSessionData();
	const spaceKey = useSpaceKey() || '';

	const [currentPageId] = usePageContentId();
	const [currentContentType] = useContentType();
	const homepageId = useSpaceHomepageId(spaceKey);
	// Blogs cannot have child content, so fall back to space homepage
	const parentContentId = currentContentType === 'blogpost' ? homepageId : currentPageId;

	const [isDismissOpen, setIsDismissOpen] = useState(false);
	const { setOnboardingState } = useSetOnboardingState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const pageAnalyticsAttributes = usePageAnalyticsAttributes();

	const isImport = IMPORTER_COMPETITORS.some((competitor) =>
		competitorName?.toLowerCase().includes(competitor),
	);

	const { data: paywallData, loading: paywallContentLoading } = useQuery<
		PromptPopupPaywallContentToDisableQuery$data,
		PromptPopupPaywallContentToDisableQuery$variables
	>(PAYWALL_CONTENT_TO_DISABLE, {
		skip: contentType !== 'whiteboard' || !isImport || edition === ConfluenceEdition.PREMIUM,
		fetchPolicy: 'no-cache',
	});
	const paywallContentToDisable = paywallData?.paywallContentToDisable;

	const displayWbImportCta =
		contentType === 'whiteboard' &&
		isImport &&
		!paywallContentLoading &&
		!(
			paywallContentToDisable?.deactivationIdentifier &&
			paywallContentToDisable?.link &&
			paywallContentToDisable?.title
		);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'viewed',
				actionSubject: 'competitorLinkPrompt',
				actionSubjectId: 'promptPopup',
				source: 'competitorLink',
				attributes: {
					linkContentType: contentType,
					linkType,
					isImport: displayWbImportCta ? 'true' : 'false',
					editorAppearance,
					...pageAnalyticsAttributes,
				},
				nonPrivacySafeAttributes: {
					linkDomain: competitorName,
					linkContentType: contentType,
					linkType,
					isImport: displayWbImportCta ? 'true' : 'false',
				},
			},
		}).fire();
	}, [
		competitorName,
		createAnalyticsEvent,
		linkType,
		pageAnalyticsAttributes,
		displayWbImportCta,
		contentType,
		editorAppearance,
	]);

	const [createContentInline, { loading: createContentLoading }] = useMutation<
		PromptPopupCreateContentInlineMutation$data,
		PromptPopupCreateContentInlineMutation$variables
	>(CREATE_CONTENT_INLINE, {
		onCompleted: (data) => {
			const contentId = data?.createContentInline?.id;
			if (!contentId) throw Error('no content id present');

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'createdByFlow',
					actionSubject: 'content',
					objectId: contentId,
					objectType: contentType,
					containerId: spaceKey,
					containerType: 'space',
					source: 'competitorLink',
					attributes: {
						flow: 'competitorLink',
						contentType,
						linkType,
						isImport: displayWbImportCta ? 'true' : 'false',
						editorAppearance,
						...pageAnalyticsAttributes,
					},
					nonPrivacySafeAttributes: {
						linkDomain: competitorName,
					},
				},
			}).fire();

			setOnboardingState({
				key: CONTENT_TYPE_ONBOARDING_KEYS[contentType],
				value: 'true',
			});

			const urlFunction = CONTENT_TYPE_URL_FUNCTIONS[contentType];
			// check valid content type
			if (urlFunction) {
				let url = urlFunction(contentId, spaceKey);
				if (displayWbImportCta) {
					const urlObj = new URL(url);
					const searchParams = new URLSearchParams(urlObj.search);
					searchParams.set(`open${competitorName}ImportTool`, 'true');
					urlObj.search = searchParams.toString();
					url = urlObj.toString();
				}

				window.open(url, '_blank', 'noreferrer, noopener');
			}
		},
	});

	const handleLinkClick = async () => {
		if (createContentLoading || !parentContentId) return;

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'clicked',
				actionSubject: 'competitorLinkPrompt',
				actionSubjectId: 'createButton',
				source: 'competitorLink',
				attributes: {
					linkContentType: contentType,
					linkType,
					isImport: displayWbImportCta ? 'true' : 'false',
					editorAppearance,
					...pageAnalyticsAttributes,
				},
				nonPrivacySafeAttributes: {
					linkDomain: competitorName,
					linkContentType: contentType,
					linkType,
					isImport: displayWbImportCta ? 'true' : 'false',
				},
			},
		}).fire();

		await createContentInline({
			variables: { createdInContentId: parentContentId, spaceKey, type: contentType },
		});
	};

	let descriptionText: string;
	if (contentType === 'database') {
		descriptionText = intl.formatMessage(databaseI18n.descriptionDefaultDatabase, {
			competitorName,
		});
	} else {
		const whiteboardDescription = displayWbImportCta
			? i18n.descriptionImportWhiteboard
			: i18n.descriptionDefaultWhiteboard;

		descriptionText = intl.formatMessage(whiteboardDescription, { competitorName });
	}

	let createText: string;
	if (contentType === 'database') {
		createText = intl.formatMessage(databaseI18n.createButtonDefaultDatabase);
	} else {
		const whiteboardCreateButton = displayWbImportCta
			? i18n.createButtonImportWhiteboard
			: i18n.createButtonDefaultWhiteboard;

		createText = intl.formatMessage(whiteboardCreateButton, { competitorName });
	}

	const dismissText = intl.formatMessage(
		contentType === 'database' ? databaseI18n.dismissButtonDatabase : i18n.dismissButton,
	);

	const handleDismissClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'competitorLinkPrompt',
				actionSubjectId: 'promptDismiss',
				source: 'competitorLink',
				attributes: {
					linkContentType: contentType,
					linkType,
					editorAppearance,
					...pageAnalyticsAttributes,
				},
			},
		}).fire();
		setOnboardingState({
			key: CONTENT_TYPE_ONBOARDING_KEYS[contentType],
			value: 'true',
		});
		setIsDismissOpen(false);
	}, [
		createAnalyticsEvent,
		setOnboardingState,
		linkType,
		pageAnalyticsAttributes,
		contentType,
		editorAppearance,
	]);

	const handleMoreMenuClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'competitorLinkPrompt',
				actionSubjectId: 'promptMoreMenu',
				source: 'competitorLink',
				attributes: {
					linkContentType: contentType,
					linkType,
					editorAppearance,
					...pageAnalyticsAttributes,
				},
			},
		}).fire();
		setIsDismissOpen(!isDismissOpen);
	}, [
		createAnalyticsEvent,
		isDismissOpen,
		linkType,
		pageAnalyticsAttributes,
		contentType,
		editorAppearance,
	]);

	const titleText =
		contentType === 'database'
			? intl.formatMessage(databaseI18n.titleDatabase)
			: intl.formatMessage(i18n.titleWhiteboard);

	// TODO: Add `shouldRenderToParent = true` to make popup experience accessible. See https://a11y.atlassian.net/browse/JCATS-788
	// shouldRenderToParent currently forced to false to prevent popup from being rendered in the wrong place
	const dismissPopup = (
		<Popup
			shouldRenderToParent={false}
			isOpen={isDismissOpen}
			onClose={() => setIsDismissOpen(false)}
			placement="bottom-start"
			content={() => (
				<Pressable
					xcss={styles.dismissPressable}
					onClick={handleDismissClick}
					testId="competitor-link-prompt-dismiss-button"
				>
					<Text>{dismissText}</Text>
				</Pressable>
			)}
			trigger={(triggerProps) => (
				<Pressable
					{...triggerProps}
					xcss={styles.showMoreIcon}
					onClick={handleMoreMenuClick}
					testId="competitor-link-prompt-dismiss-menu"
				>
					<ShowMoreHorizontalIcon label={dismissText} size="medium" />
				</Pressable>
			)}
		/>
	);

	const titleSection = (
		<Flex xcss={styles.titleContainer}>
			<Heading size="xsmall">{titleText}</Heading>
			{dismissPopup}
		</Flex>
	);

	const contentSection = (
		<Box>
			<Box xcss={styles.popupDescription}>
				<Text>{descriptionText}</Text>
			</Box>
			<Pressable onClick={handleLinkClick} xcss={styles.ctaStyles}>
				{createText}
			</Pressable>
		</Box>
	);

	return (
		<Box xcss={styles.popupBoxDatabase} testId="competitor-link-prompt-popup">
			<Flex xcss={styles.iconContainer}>
				<IconTile
					key={contentType === 'database' ? 'databaseIcon' : 'whiteboardIcon'}
					icon={contentType === 'database' ? DatabaseIcon : WhiteboardIcon}
					label={titleText}
					appearance={contentType === 'database' ? 'purpleBold' : 'tealBold'}
					size="small"
				/>
				<Box xcss={styles.textContainer}>
					{titleSection}
					{contentSection}
				</Box>
			</Flex>
		</Box>
	);
};

export const PromptPopup = ({
	competitorName,
	linkType,
	contentType,
	editorAppearance,
}: PromptComponentProps) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const pageAnalyticsAttributes = usePageAnalyticsAttributes();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'viewed',
				actionSubject: 'competitorLinkPrompt',
				actionSubjectId: 'promptIcon',
				source: 'competitorLink',
				attributes: {
					linkContentType: contentType,
					linkType,
					editorAppearance,
					...pageAnalyticsAttributes,
				},
				nonPrivacySafeAttributes: {
					linkDomain: competitorName,
				},
			},
		}).fire();
	}, [
		competitorName,
		createAnalyticsEvent,
		linkType,
		pageAnalyticsAttributes,
		contentType,
		editorAppearance,
	]);

	const handlePromptPopupClose = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'closed',
				actionSubject: 'competitorLinkPrompt',
				actionSubjectId: 'promptPopup',
				source: 'competitorLink',
				attributes: {
					linkContentType: contentType,
					linkType,
					editorAppearance,
					...pageAnalyticsAttributes,
				},
			},
		}).fire();
		setIsOpen(false);
	}, [createAnalyticsEvent, linkType, pageAnalyticsAttributes, contentType, editorAppearance]);

	const handlePromptPopupClick = useCallback(() => {
		if (isOpen) {
			handlePromptPopupClose();
		} else {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'competitorLinkPrompt',
					actionSubjectId: 'promptIcon',
					source: 'competitorLink',
					attributes: {
						linkContentType: contentType,
						linkType,
						editorAppearance,
						...pageAnalyticsAttributes,
					},
				},
			}).fire();
			setIsOpen(!isOpen);
		}
	}, [
		createAnalyticsEvent,
		handlePromptPopupClose,
		isOpen,
		linkType,
		pageAnalyticsAttributes,
		contentType,
		editorAppearance,
	]);

	return (
		// TODO: Add `shouldRenderToParent = true` to make popup experience accessible. See https://a11y.atlassian.net/browse/JCATS-788
		// shouldRenderToParent currently forced to false to prevent popup from being rendered in the wrong place
		<Popup
			shouldRenderToParent={false}
			isOpen={isOpen}
			onClose={handlePromptPopupClose}
			placement="bottom-start"
			content={() => (
				<PromptComponent
					competitorName={competitorName}
					linkType={linkType}
					contentType={contentType}
				/>
			)}
			trigger={(triggerProps) => (
				<Pressable
					{...triggerProps}
					onClick={handlePromptPopupClick}
					xcss={cx(styles.pressable, isOpen ? styles.pressableOpen : styles.pressableClosed)}
					testId="competitor-link-prompt-icon"
				>
					<LightbulbIcon
						color={token('color.icon.discovery')}
						label={intl.formatMessage(
							contentType === 'database' ? databaseI18n.titleDatabase : i18n.titleWhiteboard,
						)}
					/>
				</Pressable>
			)}
		/>
	);
};

export const PromptPopupContainer = ({
	sourceUrl,
	linkType,
	editorAppearance,
}: PromptComponentContainerProps) => {
	const {
		data: onboardingStateData,
		loading,
		error,
	} = useGetOnboardingState([
		PROMPT_WHITEBOARD_COMPETITOR_LINK_DISMISSED.PROMPT_DISMISSED,
		PROMPT_DATABASE_COMPETITOR_LINK.DISMISSED,
	]);
	const { isPromptDismissed: isPromptWhiteboardDismissed, isPromptDatabaseDismissed } =
		deserializeState(onboardingStateData);
	const { competitorName: whiteboardCompetitorName } = useIsUrlWhiteboardCompetitor(sourceUrl);
	const { databaseCompetitorName } = useIsEligibleDatabaseCompetitor(sourceUrl);

	let competitorContentType: PromptContentType | undefined = undefined;
	if (whiteboardCompetitorName) {
		competitorContentType = 'whiteboard';
	} else if (databaseCompetitorName) {
		competitorContentType = 'database';
	}
	const competitorName = whiteboardCompetitorName || databaseCompetitorName || undefined;

	const validLinkType =
		linkType && (linkType === 'inline' || linkType === 'card' || linkType === 'embed')
			? linkType
			: undefined;

	if (competitorContentType === 'database') {
		return (
			!loading &&
			!error &&
			!isPromptDatabaseDismissed &&
			competitorName &&
			expValEquals('cc_fd_prompt_database_competitor_link', 'cohort', 'experiment') && (
				<Box xcss={styles.promptContainer}>
					<PromptPopup
						competitorName={competitorName}
						linkType={validLinkType}
						contentType={competitorContentType}
						editorAppearance={editorAppearance}
					/>
				</Box>
			)
		);
	}

	return (
		!loading &&
		!error &&
		!isPromptWhiteboardDismissed &&
		competitorName &&
		competitorContentType === 'whiteboard' &&
		expValEquals('prompt_whiteboard_competitor_link', 'cohort', 'experiment') && (
			<Box xcss={styles.promptContainer}>
				<PromptPopup
					competitorName={competitorName}
					linkType={validLinkType}
					contentType={competitorContentType}
					editorAppearance={editorAppearance}
				/>
			</Box>
		)
	);
};
